import React, { useState } from 'react';

import { Button, Grid } from '@mui/material';
import { Box } from '@mui/system';
import { GridColDef, GridRowsProp } from '@mui/x-data-grid-pro';

import { EditRemoveBtn } from 'components/buttons/EditRemoveBtn';
import { CissNumberField, CissTextField } from 'components/fields';
import { Row } from 'components/form';
import { PanelGrid } from 'components/grid';
import { ContentModal } from 'components/modal';
import { Block } from 'components/page/Block';
import { SubTitle } from 'components/text';
import { useFormContext } from 'context/FormContext';
import { HeaderColunasOpcoes } from 'pages/cadastro/colunas-dinamicas/components/atoms/HeaderColunasOpcoes';
import { RemoveEditOpcaoBtn } from 'pages/cadastro/colunas-dinamicas/components/atoms/RemoveEditOpcaoBtn';
import { useGetColunasOpcoes } from 'pages/cadastro/colunas-dinamicas/hooks/useGetColunasOpcoes';
import { useMutateColunasDinamicas } from 'pages/cadastro/colunas-dinamicas/hooks/useMutateColunasDinamicas';

interface OpcaoProps {
    idConfiguracaoPropriedadeOpcao: number;
    dsConfiguracaoPropriedadeOpcao: string;
    vlConfiguracaoPropriedadeOpcao?: string;
    dsChave: string;
    nrSequencia: string;
}

const gridRows = (rows: OpcaoProps[] | undefined): GridRowsProp => {
    if (!rows) return [];
    return rows.map((row, index) => {
        return {
            id: index + 1,
            idConfiguracaoPropriedadeOpcao: row.idConfiguracaoPropriedadeOpcao,
            dsConfiguracaoPropriedadeOpcao: row.dsConfiguracaoPropriedadeOpcao,
            dsChave: row.dsChave || row.vlConfiguracaoPropriedadeOpcao,
            nrSequencia: row.nrSequencia,
        };
    });
};

export function ListColunasOpcoes() {
    const { isLoading } = useFormContext();
    const [openAddModal, setOpenAddModal] = useState(false);
    const [filterValue, setFilterValue] = useState('');
    const [opcaoData, setOpcaoData] = useState<OpcaoProps | null>(null);
    const { filteredList, handleRemoveOption, handleSaveOption } = useGetColunasOpcoes(filterValue);

    const handleCloseModal = () => {
        setOpenAddModal(false);
        setOpcaoData(null);
    };

    const columns: GridColDef[] = [
        {
            field: 'idConfiguracaoPropriedadeOpcao',
            headerName: 'Código',
            type: 'number',
            width: 80,
            minWidth: 70,
        },
        {
            field: 'dsConfiguracaoPropriedadeOpcao',
            headerName: 'Opção',
            minWidth: 110,
            flex: 1,
        },
        {
            field: 'nrSequencia',
            headerName: 'Ordem',
            minWidth: 110,
            flex: 1,
        },
        {
            field: 'acoes',
            align: 'center',
            headerAlign: 'center',
            headerName: 'Ações',
            filterable: false,
            sortable: false,
            hideable: false,
            disableColumnMenu: true,
            minWidth: 90,
            renderCell: ({ row }) => (
                <RemoveEditOpcaoBtn
                    id={row.idConfiguracaoPropriedadeOpcao}
                    sx={{ ml: 'auto', p: 1.5 }}
                    removeBtnAction={() => handleRemoveOption(row.idConfiguracaoPropriedadeOpcao)}
                    editBtnAction={() => {
                        setOpenAddModal((prevState) => !prevState);
                        setOpcaoData(row);
                    }}
                />
            ),
        },
    ];

    return (
        <>
            <Block sx={{ display: 'flex', flex: 1, flexDirection: 'column' }}>
                <SubTitle label="Opções" />

                <HeaderColunasOpcoes
                    searchPlaceholder="Pesquisar"
                    onClickAdd={() => setOpenAddModal(true)}
                    setValueFilter={(e) => {
                        setFilterValue(e);
                    }}
                />

                <PanelGrid
                    rows={gridRows(filteredList)}
                    rowsCount={filteredList?.length}
                    columns={columns}
                    loading={Boolean(isLoading)}
                    reload={false}
                />
            </Block>

            <ContentModal open={openAddModal} setOpen={setOpenAddModal}>
                <Box>
                    <SubTitle label={!opcaoData?.idConfiguracaoPropriedadeOpcao ? 'Cadastrar nova opção' : 'Editar opção'} />

                    <Row>
                        <Grid item flex={1}>
                            <CissTextField
                                label="Chave"
                                value={opcaoData?.vlConfiguracaoPropriedadeOpcao || opcaoData?.dsChave}
                                onChange={(e) =>
                                    setOpcaoData((prevState: any) => {
                                        return { ...prevState, dsChave: e.target.value };
                                    })
                                }
                            />
                        </Grid>

                        <Grid item flex={1}>
                            <CissNumberField
                                label="Ordem"
                                value={opcaoData?.nrSequencia || null}
                                onChange={(e) =>
                                    setOpcaoData((prevState: any) => {
                                        return { ...prevState, nrSequencia: e.target.value };
                                    })
                                }
                            />
                        </Grid>

                        <Grid item flex={2}>
                            <CissTextField
                                label="Nome da opção"
                                value={opcaoData?.dsConfiguracaoPropriedadeOpcao}
                                onChange={(e) =>
                                    setOpcaoData((prevState: any) => {
                                        return { ...prevState, dsConfiguracaoPropriedadeOpcao: e.target.value };
                                    })
                                }
                            />
                        </Grid>
                    </Row>
                    <Box sx={{ alignSelf: 'flex-end', flex: 1, display: 'flex', my: '10px', justifyContent: 'flex-end', gap: 2 }}>
                        <Button sx={{ width: '100px' }} onClick={handleCloseModal} variant="outlined">
                            Cancelar
                        </Button>

                        <Button
                            sx={{ width: '100px' }}
                            variant="contained"
                            onClick={() => {
                                handleSaveOption(opcaoData);
                                handleCloseModal();
                            }}
                            disabled={isLoading}
                        >
                            Salvar
                        </Button>
                    </Box>
                </Box>
            </ContentModal>
        </>
    );
}
